import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bot, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Copy, KeyRound, ShieldOff,
  UserPlus, Unplug, Trash2, User,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/social/planner`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Line = ({ label, value, testid, tone }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0">
    <span className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] w-[170px] shrink-0">{label}</span>
    <span className="text-[12.5px] break-all" style={{ color: tone || "#1A1A1A" }} data-testid={testid}>{value}</span>
  </div>
);

const Pill = ({ tone, children, testid }) => (
  <span className="inline-flex items-center gap-1 mono text-[10.5px] font-bold px-2 py-0.5 rounded-full"
    style={tone} data-testid={testid}>{children}</span>
);

const ON = { background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" };
const OFF = { background: "#F4F2ED", border: "1px solid #DAD5CA", color: "#8A857A" };
const WARN = { background: "#FFF8D6", border: "1px solid #E8C86A", color: "#6B5600" };

/* People sign in to Claude themselves and it acts as them, with their
   permissions. This card is the gate on using it at all, plus whoever is
   currently connected — and the unattended machine key beneath them. */
export default function PlannerConnectorCard() {
  const [h, setH] = useState(null);
  const [people, setPeople] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [fresh, setFresh] = useState("");
  const [adding, setAdding] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [conn, folk] = await Promise.all([
        axios.get(`${API}/connection`), axios.get(`${API}/people`)]);
      setH(conn.data); setPeople(folk.data);
    } catch (e) {
      setH({ ready: false, reason: e?.response?.data?.detail || "Could not read the connector" });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { const r = await fn(); toast.success(r?.data?.note || msg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
  };

  const issue = async (rotate) => {
    if (rotate && !window.confirm(
      "Rotating the machine key stops anything using it immediately. Carry on?")) return;
    setIssuing(true);
    try {
      const r = await axios.post(`${API}/key`, { rotate });
      setFresh(r.data.key);
      toast.success(rotate ? "Key rotated — update whatever was using it" : "Machine key issued");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not issue a key"); }
    finally { setIssuing(false); }
  };

  const copy = (text, what) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${what} copied`);
  };

  const url = typeof window !== "undefined"
    ? `${window.location.origin}${h?.path || "/api/mcp"}` : h?.url || "";
  const rows = people?.people || [];
  const connected = rows.filter((p) => p.connected).length;

  return (
    <Card className="bg-white border-[#DAD5CA]" data-testid="planner-connector-card">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Bot className="w-4 h-4 text-[#59554D] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold text-[#1A1A1A]">EQUIPR Planner — Claude connector</h3>
              <p className="text-[12.5px] text-[#59554D]">
                People sign in with their work email and the usual 6-digit code, and Claude then
                acts as them on the Social Calendar — their name on every change, their
                permissions, nothing more.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="h-8 border-[#DAD5CA] text-[#59554D] shrink-0" data-testid="planner-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="planner-state">
          <Pill tone={connected ? ON : WARN}>
            {connected ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {connected ? `${connected} CONNECTED` : "NOBODY CONNECTED YET"}
          </Pill>
          <span className="text-[11.5px] text-[#8A857A]" data-testid="planner-usage">
            {h?.claude_changes_today ?? 0} change{(h?.claude_changes_today ?? 0) === 1 ? "" : "s"} through
            Claude today
          </span>
        </div>

        <div className="mt-3 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-2.5"
          data-testid="planner-setup-steps">
          <div className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">Adding it in Claude</div>
          <ol className="text-[12.5px] text-[#59554D] mt-1.5 space-y-1 list-decimal pl-4">
            <li>Settings → Connectors → <b>Add custom connector</b>, and paste the URL below.</li>
            <li>Advanced → Transport: <b>Streamable HTTP</b>. Add it.</li>
            <li>Press <b>Connect</b> or <b>Sign in</b> — an EQUIPR page opens. Enter your work
              email, then the 6-digit code we email you, and allow it.</li>
            <li>Ask Claude "who are you acting as?" to prove it signed you in.</li>
          </ol>
          <p className="text-[12px] text-[#8A857A] mt-1.5">
            You have to be on the list below first, and you sign in yourself — nobody can connect
            on your behalf.
          </p>
        </div>

        <div className="mt-3">
          <Line label="Connector URL" value={url || "—"} testid="planner-url" />
          <Line label="Protocol" value={h?.protocol ? `MCP ${h.protocol} · streamable HTTP · OAuth sign-in` : "—"}
            testid="planner-protocol" />
          <Line label="Closed to everyone" value={(h?.closed_to_everyone || []).join(" · ") || "—"}
            testid="planner-closed" />
          <Line label="Idle connections end after"
            value={`${people?.idle_days ?? 30} days without use — renews silently while in use`}
            testid="planner-idle" />
        </div>

        {/* ── Who may connect ─────────────────────────────────────────── */}
        <div className="mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[13px] font-bold text-[#1A1A1A]">Who may use Claude</h4>
            <span className="text-[11.5px] text-[#8A857A]">
              Being on this list is permission to use Claude, not permission to do anything new.
            </span>
          </div>

          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-[12.5px]" style={{ minWidth: 620 }}
              data-testid="planner-people-table">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-[0.08em] text-[#8A857A] text-left">
                  <th className="py-1.5 font-semibold">Person</th>
                  <th className="py-1.5 font-semibold">As them, Claude can</th>
                  <th className="py-1.5 font-semibold">Connected</th>
                  <th className="py-1.5 font-semibold">Last used</th>
                  <th className="py-1.5 font-semibold text-right">Changes</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="py-3 text-[#8A857A]" data-testid="planner-people-empty">
                    Nobody can connect Claude yet. Add someone below.
                  </td></tr>
                )}
                {rows.map((p) => (
                  <tr key={p.id} className="border-t border-[#F4F2ED]" data-testid={`planner-person-${p.id}`}>
                    <td className="py-2 pr-2">
                      <span className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-[#8A857A]" />
                        <span>
                          <b className="text-[#1A1A1A]">{p.name}</b>
                          <span className="block text-[11.5px] text-[#8A857A]">{p.email}</span>
                        </span>
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-[#59554D]">
                      {p.can_approve ? "draft, submit and approve" : "draft and submit"}
                      {!p.active && <span className="block text-[11.5px] text-[#8E1212]">account inactive</span>}
                    </td>
                    <td className="py-2 pr-2">
                      {p.connected
                        ? <Pill tone={ON} testid={`planner-person-on-${p.id}`}>SIGNED IN</Pill>
                        : p.expired
                          ? <Pill tone={WARN}>EXPIRED</Pill>
                          : <Pill tone={OFF}>NOT YET</Pill>}
                      {p.connected && (
                        <span className="block text-[11px] text-[#8A857A] mt-0.5">
                          {when(p.connected_at)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-[#59554D]">{when(p.last_used_at)}</td>
                    <td className="py-2 pr-2 text-right text-[#59554D]">{p.changes}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {p.connected && (
                        <button type="button" title="Cut this connection off now"
                          data-testid={`planner-revoke-${p.id}`}
                          onClick={() => window.confirm(`Cut ${p.name}'s Claude connection off now?`)
                            && act(() => axios.delete(`${API}/connections/${p.connection_id}`),
                              "Connection cut off")}
                          className="p-1 rounded hover:bg-[#F4F2ED] text-[#8E1212]">
                          <Unplug className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button type="button" title="Remove from the list"
                        data-testid={`planner-remove-${p.id}`}
                        onClick={() => window.confirm(
                          `Remove ${p.name} from the planner list? Any live connection is cut off.`)
                          && act(() => axios.delete(`${API}/people/${p.id}`), "Removed")}
                        className="p-1 rounded hover:bg-[#F4F2ED] text-[#59554D]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <select value={adding} onChange={(e) => setAdding(e.target.value)}
              data-testid="planner-add-select"
              className="h-8 rounded-md border border-[#DAD5CA] bg-white px-2 text-[12.5px] text-[#1A1A1A]">
              <option value="">Add someone…</option>
              {(people?.candidates || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {c.role}</option>
              ))}
            </select>
            <Button size="sm" disabled={!adding} data-testid="planner-add-person"
              onClick={() => act(() => axios.post(`${API}/people`, { agent_id: adding }),
                "Added").then(() => setAdding(""))}
              className="h-8 bg-[#FFD108] hover:bg-[#F0C400] text-black">
              <UserPlus className="w-3.5 h-3.5" />
              <span className="ml-1.5 text-[12px] font-bold uppercase">Add</span>
            </Button>
          </div>
        </div>

        {/* ── The machine key, demoted ────────────────────────────────── */}
        <div className="mt-4 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3"
          data-testid="planner-machine-key">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[13px] font-bold text-[#1A1A1A]">Machine key</h4>
            <Pill tone={h?.key_set ? ON : OFF} testid="planner-key-state">
              {h?.key_set ? `ISSUED · ENDS ${h.key_last4}` : "NOT ISSUED"}
            </Pill>
            <span className="text-[11.5px] text-[#8A857A]">
              For scripts and anything unattended — not how a person connects.
            </span>
          </div>
          <p className="text-[12px] text-[#59554D] mt-1.5">
            {h?.machine_key_note || ""} It is stored only as a digest, so it can never be read
            back — rotate to issue a new one.
            {h?.key_issued_by ? ` Issued by ${h.key_issued_by}.` : ""}
          </p>
          {fresh && (
            <div className="mt-2 rounded-md border border-[#E8C86A] bg-[#FFF8D6] p-2.5"
              data-testid="planner-new-key">
              <p className="text-[12.5px] font-semibold text-[#6B5600]">
                Copy this now — it is not shown again.
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="mono text-[12px] break-all text-[#1A1A1A]">{fresh}</span>
                <button type="button" onClick={() => copy(fresh, "Machine key")}
                  data-testid="planner-copy-key" className="shrink-0 p-1 rounded hover:bg-[#F3E7B0]">
                  <Copy className="w-3.5 h-3.5 text-[#59554D]" />
                </button>
              </div>
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {!h?.key_set ? (
              <Button size="sm" onClick={() => issue(false)} disabled={issuing}
                className="h-8 bg-[#FFD108] hover:bg-[#F0C400] text-black" data-testid="planner-issue-key">
                {issuing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                <span className="ml-1.5 text-[12px] font-bold uppercase">Issue a key</span>
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => issue(true)} disabled={issuing}
                  className="h-8 border-[#DAD5CA] text-[#59554D]" data-testid="planner-rotate-key">
                  {issuing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                  <span className="ml-1.5 text-[12px]">Rotate</span>
                </Button>
                <Button size="sm" variant="outline" data-testid="planner-revoke-key"
                  onClick={() => window.confirm("Switch the machine key off?")
                    && act(() => axios.delete(`${API}/key`), "Machine key switched off")}
                  className="h-8 border-[#E8C4C4] text-[#8E1212]">
                  <ShieldOff className="w-3.5 h-3.5" />
                  <span className="ml-1.5 text-[12px]">Switch off</span>
                </Button>
              </>
            )}
            {url && (
              <button type="button" onClick={() => copy(url, "Connector URL")}
                data-testid="planner-copy-url"
                className="text-[12px] font-semibold text-[#59554D] underline">
                Copy the URL
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
